import React, { useRef, useState } from 'react';

import { fetch_payout_response, post_payout_response_apply, upload_payout_response } from '../api';
import { apply_confirm_text, payout_error, response_file_error, response_view } from './adapters';
import { upload_signature } from './requests';
import LinesTable from './lines_table';
import useIdempotentPost from './use_idempotent_post';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { LINE_COLUMNS, payouts_csv_name } from './csv';

/*
 * the bank's response file: upload (the same bytes again answer the earlier upload), a per-line
 * preview, then Apply after an in-page confirm.
 */
const UPLOAD_FAILED_TEXT = 'The file may not have been read. Upload it again to check.';
const APPLY_FAILED_TEXT =
  'The response may not have been applied. Upload the same file again to check.';
const RELOAD_FAILED_TEXT = 'Could not read the refreshed preview. Upload the same file again.';

const Summary = ({ view }) => {
  const s = view.summary;
  return (
    <div className="tp-po-summary">
      <span>{s.lines} lines</span>
      <span className="tp-pos">
        {s.sent} sent ({s.sent_total})
      </span>
      <span className="tp-neg">
        {s.failed} failed ({s.failed_total})
      </span>
      <span>{s.held} held for Ops</span>
      <span>{s.ignored} ignored</span>
      <span>{s.no_change} unchanged</span>
    </div>
  );
};

const ResponsePanel = ({ pay_month, on_open_teacher, on_notify, on_error }) => {
  const [file, setFile] = useState(null);
  const [file_error, setFileError] = useState('');
  const [view, setView] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [notice, setNotice] = useState({ tone: '', text: '' });
  const input_ref = useRef(null);
  const upload = useIdempotentPost(UPLOAD_FAILED_TEXT);

  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
    setFileError('');
  };

  const do_upload = () => {
    const err = response_file_error(file);
    setFileError(err);
    if (err || upload.busy) return;
    setNotice({ tone: '', text: '' });
    upload
      .send(upload_signature(pay_month, file), (id) => upload_payout_response(pay_month, id, file))
      .then((r) => {
        if (r.ok) {
          setView(response_view(r.data));
          setConfirming(false);
          if (input_ref.current) input_ref.current.value = '';
          setFile(null);
          return;
        }
        if (r.err.kind === 'auth') on_error(r.raw, UPLOAD_FAILED_TEXT);
        setFileError(r.err.text);
      });
  };

  const refresh_preview = (text, fresh) => {
    if (fresh) {
      setView(response_view(fresh));
      setNotice({ tone: 'warn', text });
      return;
    }
    fetch_payout_response(view.response_uuid)
      .then((d) => {
        setView(response_view(d));
        setNotice({ tone: 'warn', text });
      })
      .catch((e) => {
        const v = payout_error(e, RELOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, RELOAD_FAILED_TEXT);
        setNotice({ tone: 'error', text: `${text} ${v.text}` });
      });
  };

  const apply = () => {
    if (applying) return;
    setApplying(true);
    post_payout_response_apply(view.response_uuid)
      .then((d) => {
        setApplying(false);
        setConfirming(false);
        const v = response_view(d);
        setView(v);
        on_notify(
          'success',
          `Bank response applied: ${v.summary.sent} sent, ${v.summary.failed} failed, ${v.summary.held} held for Ops.`
        );
      })
      .catch((e) => {
        setApplying(false);
        setConfirming(false);
        const v = payout_error(e, APPLY_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, APPLY_FAILED_TEXT);
        if (v.kind === 'preview_changed') refresh_preview(v.text, v.fresh);
        else setNotice({ tone: 'error', text: v.text });
      });
  };

  return (
    <div className="tp-po-response">
      <div className="tp-hint tp-po-intro">
        Upload the response file the bank returns for {pay_month} (16 columns, no header). Nothing
        changes until you apply it.
      </div>
      <div className="tp-frow">
        <label htmlFor="tp-po-resp-file" className="tp-hint">
          Bank response file
        </label>
        <input
          id="tp-po-resp-file"
          ref={input_ref}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          onChange={pick}
        />
        {/* Apply is the one primary while a preview waits for it. */}
        <button
          type="button"
          className={view && !view.applied ? 'tp-btn-out' : 'tp-btn'}
          onClick={do_upload}
          aria-disabled={upload.busy || !file ? 'true' : undefined}
        >
          {upload.busy ? 'Reading…' : 'Upload and preview'}
        </button>
      </div>
      {file_error ? (
        <div className="tp-neg" role="alert">
          {file_error}
        </div>
      ) : null}

      {view ? (
        <div className="tp-po-preview">
          <h3 className="tp-po-h3">
            {view.file_name} · uploaded by {view.uploaded_by} at {view.uploaded_at}
          </h3>
          {view.already_uploaded && !view.applied ? (
            <div className="tp-banner tp-banner-info">
              This file was uploaded before; this is that upload&apos;s preview.
            </div>
          ) : null}
          {view.applied ? (
            <div className="tp-banner tp-banner-ok" role="status">
              Applied{view.applied_at ? ` at ${view.applied_at}` : ''}. Held lines wait under Held
              lines.
            </div>
          ) : null}
          {view.summary.double_payment_alerts ? (
            <div className="tp-banner tp-banner-error" role="alert">
              {view.summary.double_payment_alerts}{' '}
              {view.summary.double_payment_alerts === 1 ? 'line comes' : 'lines come'} from a
              cancelled file: the bank may have paid a teacher twice. They are held for Ops.
            </div>
          ) : null}
          {notice.text ? (
            <div
              className={`tp-banner ${notice.tone === 'error' ? 'tp-banner-error' : 'tp-banner-warn'}`}
              role="alert"
            >
              {notice.text}
            </div>
          ) : null}
          <Summary view={view} />
          <div className="tp-po-csv-row">
            <DownloadCsvButton
              columns={LINE_COLUMNS}
              rows={view.lines}
              file_name={payouts_csv_name('Bank response', view.pay_month || pay_month)}
            />
          </div>
          <LinesTable rows={view.lines} on_open_teacher={on_open_teacher} />
          {!view.applied ? (
            <div className="tp-po-confirm">
              {confirming ? (
                <>
                  <div className="tp-banner tp-banner-warn" role="alert">
                    {apply_confirm_text(view)}
                  </div>
                  <div className="tp-modal-ft">
                    <button
                      type="button"
                      className="tp-btn-ghost"
                      onClick={applying ? undefined : () => setConfirming(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="tp-btn"
                      onClick={apply}
                      aria-disabled={applying ? 'true' : undefined}
                    >
                      {applying ? 'Applying…' : 'Yes, apply'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="tp-modal-ft">
                  <button type="button" className="tp-btn" onClick={() => setConfirming(true)}>
                    Apply…
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ResponsePanel;
